import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../utils/blogData';
import Icon from '../../components/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const categories = useMemo(() => {
    const cats = ['All', ...new Set(blogPosts.map(post => post.category))];
    return cats;
  }, []);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
      const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const featuredPost = blogPosts[0];
  const remainingPosts = filteredPosts.filter(post => post.id !== featuredPost.id || selectedCategory !== 'All' || searchQuery !== '');

  return (
    <>
      <Helmet>
        <title>Blog | Dishant Madariya - Technical Insights & Tutorials</title>
        <meta name="description" content="Explore 15+ articles on React, Node.js, AI, and Cloud Architecture. In-depth guides for modern software engineers." />
        <meta name="keywords" content="Dishant Madariya Blog, Web Development, React, Node.js, AI, TypeScript, Software Engineering" />
      </Helmet>

      <div className="min-h-screen bg-background pt-32 pb-20">
        <SectionScrollIndicator />
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Header & Search */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-5xl md:text-6xl font-black text-text-primary mb-6 leading-tight"
              >
                Insights for the <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-500">Modern Developer</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl text-text-secondary"
              >
                Deep dives into architecture, performance, and the future of web technology.
              </motion.p>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative w-full md:w-80 group"
            >
              <input 
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-surface border border-border rounded-xl py-4 pl-12 pr-4 text-text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-300 outline-none"
              />
              <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary group-focus-within:text-primary transition-colors" />
            </motion.div>
          </div>

          {/* Featured Post (only show if no filter/search) */}
          {selectedCategory === 'All' && searchQuery === '' && (
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative group mb-20 overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title} 
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden" />
                </div>
                <div className="p-8 lg:p-16 flex flex-col justify-center">
                  <div className="inline-flex items-center space-x-2 text-primary font-bold mb-6 uppercase tracking-widest text-sm">
                    <span className="px-3 py-1 bg-primary/10 rounded-full">Featured Post</span>
                    <span>•</span>
                    <span className="text-text-secondary font-medium tracking-normal capitalize">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-text-primary mb-6 group-hover:text-primary transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-lg text-text-secondary mb-8 line-clamp-3">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-text-primary font-bold text-sm">{featuredPost.author}</p>
                        <p className="text-text-secondary text-xs">{featuredPost.date}</p>
                      </div>
                    </div>
                    <Link 
                      to={`/blog/${featuredPost.slug}`}
                      className="px-6 py-3 bg-primary text-white font-bold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all duration-300"
                    >
                      Read Now
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Category Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-10 overflow-x-auto pb-4 no-scrollbar">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 border ${
                  selectedCategory === cat 
                  ? 'bg-primary border-primary text-white shadow-lg shadow-primary/20' 
                  : 'bg-surface border-border text-text-secondary hover:border-primary hover:text-primary'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {remainingPosts.map((post, index) => (
                <motion.article 
                  key={post.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="group flex flex-col bg-surface rounded-2xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-2"
                >
                  <Link to={`/blog/${post.slug}`} className="block overflow-hidden aspect-[4/3] relative">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 dark:bg-black/80 backdrop-blur-md rounded-lg text-xs font-bold text-primary shadow-sm">
                        {post.category}
                      </span>
                    </div>
                  </Link>
                  
                  <div className="flex-1 p-8 flex flex-col">
                    <div className="flex items-center space-x-2 text-xs text-text-secondary font-bold uppercase tracking-widest mb-4">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>

                    <h2 className="text-xl font-bold text-text-primary mb-4 group-hover:text-primary transition-colors duration-300 line-clamp-2 min-h-[3.5rem]">
                      <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                    </h2>

                    <p className="text-text-secondary text-sm line-clamp-3 mb-8 flex-1 leading-relaxed">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-border/50">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Icon name="User" size={16} className="text-primary" />
                        </div>
                        <span className="text-xs font-bold text-text-primary">{post.author}</span>
                      </div>
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="p-2 bg-background rounded-lg text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-sm"
                      >
                        <Icon name="ArrowRight" size={18} />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-surface rounded-full mb-6 border border-border">
                <Icon name="SearchX" size={40} className="text-text-secondary" />
              </div>
              <h3 className="text-2xl font-bold text-text-primary mb-2">No results found</h3>
              <p className="text-text-secondary">We couldn't find any articles matching your search.</p>
              <button 
                onClick={() => {setSearchQuery(''); setSelectedCategory('All');}}
                className="mt-6 text-primary font-bold hover:underline"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;
